import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import './App.css';
import { useTheme } from './hooks/useTheme';
import Header from './components/Header';
import AuthPage from './components/AuthPage';
import LeftPanel from './components/LeftPanel';
import RightPanel from './components/RightPanel';
import SongLibrary from './components/SongLibrary';
import MidiParser from 'midi-parser-js';
import { parseMidiChords } from './utils/midiChordDetector';
import { alignChordsToLyrics, computeTimedLyrics, addSectionStartChords, injectSpacers } from './utils/chordAlignment';
import { rationalizeAlignment } from './utils/chordRationalize';
import { loadLibraryRemote, saveSongRemote, migrateLocalLibraryToServer } from './utils/songLibrary';
import { readLegacySongFromCurrentUrl, readSharedSongFromCurrentUrl } from './utils/shareUrl';
import { apiFetch, apiJson, apiUrl } from './utils/apiClient';
import { addSectionTags, addSectionTagsFromBoundaries, injectInstrumentalTags } from './utils/sectionTags';
import { parseChordSheet } from './utils/chordSheetParser';
import { buildSurfaceParity, buildTimingAudit, normalizeAlignmentTimeFirst, fetchPhonemeGraphemes } from './utils/alignmentLayout';
import { buildTimingEditDiagnostics } from './utils/timingEdit';

const DRAFT_STORAGE_KEY = 'chorddetector_recovery_draft_v1';
const MAX_DEBUG_WORDS = 1000;
const MAX_DEBUG_LAYOUT_LINES = 160;
const MAX_DEBUG_ANCHORS_PER_LINE = 24;

function compactRawAnalysisPayload(raw) {
  if (!raw || typeof raw !== 'object') return raw;
  const out = { ...raw };

  if (Array.isArray(out.rawBeatTimes) && out.rawBeatTimes.length) {
    // Keep one beat representation in memory; beats already contain time values.
    delete out.rawBeatTimes;
  }

  if (Array.isArray(out.whisperWords) && out.whisperWords.length > MAX_DEBUG_WORDS) {
    out.whisperWordsTruncated = out.whisperWords.length - MAX_DEBUG_WORDS;
    out.whisperWords = out.whisperWords.slice(0, MAX_DEBUG_WORDS);
  }

  if (Array.isArray(out.splitDecisionDiagnostics) && out.splitDecisionDiagnostics.length > MAX_DEBUG_LAYOUT_LINES) {
    out.splitDecisionDiagnosticsTruncated = out.splitDecisionDiagnostics.length - MAX_DEBUG_LAYOUT_LINES;
    out.splitDecisionDiagnostics = out.splitDecisionDiagnostics.slice(0, MAX_DEBUG_LAYOUT_LINES);
  }

  if (Array.isArray(out.anchorPlacements) && out.anchorPlacements.length) {
    const fullCount = out.anchorPlacements.reduce(
      (sum, line) => sum + (Array.isArray(line?.anchors) ? line.anchors.length : 0),
      0,
    );
    out.anchorPlacementCount = fullCount;
    out.anchorPlacements = out.anchorPlacements
      .slice(0, MAX_DEBUG_LAYOUT_LINES)
      .map((line) => ({
        ...line,
        anchors: Array.isArray(line?.anchors)
          ? line.anchors.slice(0, MAX_DEBUG_ANCHORS_PER_LINE)
          : [],
      }));
    const keptCount = out.anchorPlacements.reduce(
      (sum, line) => sum + (Array.isArray(line?.anchors) ? line.anchors.length : 0),
      0,
    );
    out.anchorPlacementsTruncated = Math.max(0, fullCount - keptCount);
  }

  if (Array.isArray(out.allChords)) {
    out.allChordCount = out.allChords.length;
    if (out.allChords.length > 500) out.allChords = out.allChords.slice(0, 500);
  }
  if (Array.isArray(out.sourceDetectedChords)) {
    out.sourceDetectedChordCount = out.sourceDetectedChords.length;
    if (out.sourceDetectedChords.length > 500) out.sourceDetectedChords = out.sourceDetectedChords.slice(0, 500);
  }
  if (Array.isArray(out.chords) && out.chords.length > 1200) {
    out.chordsTruncated = out.chords.length - 1200;
    out.chords = out.chords.slice(0, 1200);
  }

  return out;
}

function App() {
  const { theme, toggle: toggleTheme } = useTheme();

  // Attempt to load shared song from URL on first render
  const sharedSong = (() => { try { return readLegacySongFromCurrentUrl(); } catch { return null; } })();

  const [audioFileName, setAudioFileName] = useState('');
  const [songTitle, setSongTitle] = useState(sharedSong?.title || '');
  const [lyrics, setLyrics] = useState(sharedSong?.lyrics || '');
  const [alignment, setAlignment] = useState(sharedSong?.alignment || []);
  const [songKey, setSongKey] = useState(sharedSong?.songKey || '');
  const [bpm, setBpm] = useState(sharedSong?.bpm || null);
  const [beatsPerMeasure, setBeatsPerMeasure] = useState(sharedSong?.beatsPerMeasure || null);
  const [rawAnalysis, setRawAnalysis] = useState(null);
  const [timedLyrics, setTimedLyrics] = useState(sharedSong?.timedLyrics || []);
  const [transposition, setTransposition] = useState(sharedSong?.transposition || 0);
  const [capo, setCapo] = useState(sharedSong?.capo || 0);
  const [simplified, setSimplified] = useState(false);
  const [highlightWordsDuringPlayback, setHighlightWordsDuringPlayback] = useState(true);
  const [timingEditMode, setTimingEditMode] = useState(false);
  const [autoFollowPlayback, setAutoFollowPlayback] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(null); // { step, pct }
  const [transcribing, setTranscribing] = useState(false);
  const [importingUrl, setImportingUrl] = useState(false);
  const [error, setError] = useState('');
  const [pendingFile, setPendingFile] = useState(null);
  const [audioId, setAudioId] = useState(null); // ID of server-cached audio from URL import
  const [rationalizedAlignment, setRationalizedAlignment] = useState(null);
  const [rationalizationCount, setRationalizationCount] = useState(0);
  const [editedAlignment, setEditedAlignment] = useState(null);
  const [stemSeparate, setStemSeparate] = useState(false);
  const [audioObjectUrl, setAudioObjectUrl] = useState(null);
  const [instrumentalAudioObjectUrl, setInstrumentalAudioObjectUrl] = useState(null);
  const [vocalsAudioObjectUrl, setVocalsAudioObjectUrl] = useState(null);
  const [midiChordSource, setMidiChordSource] = useState(null); // { name, events, key, bpm }
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [library, setLibrary] = useState([]);
  const [authUser, setAuthUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const abortControllerRef = useRef(null);
  const restoredDraftRef = useRef(false);
  const lastBeatRebuildSignatureRef = useRef(null);

  const normalizeAlignmentWithContext = useCallback((inputAlignment, lyricText = lyrics, timed = timedLyrics, opts = {}) => {
    const { alignment: normalized, diagnostics } = normalizeAlignmentTimeFirst({
      lyrics: lyricText || '',
      alignment: inputAlignment || [],
      timedLyrics: timed || [],
      bpm: opts?.bpm ?? bpm ?? null,
    });
    return { normalized, diagnostics };
  }, [lyrics, timedLyrics, bpm]);

  const buildParityDiagnostics = useCallback((lyricText, aligned, timed = timedLyrics) => {
    const parity = buildSurfaceParity(lyricText || '', aligned || []);
    const timingAudit = buildTimingAudit(aligned || [], timed || []);
    const lineLayouts = parity.model
      .filter((row) => row.type === 'lyric')
      .map((row) => ({
        lineIndex: row.lineIndex,
        alignedEvents: row.alignedEvents || [],
        splitDecisions: row.splitDecisions || [],
        chordRow: row.chordRow || '',
        lyricRow: row.lyricRow || row.text || '',
      }));
    return {
      hash: parity.renderParityHash,
      webHash: parity.web,
      textHash: parity.text,
      pdfHash: parity.pdf,
      diff: parity.renderParityDiff,
      lineCount: lineLayouts.length,
      lineLayouts,
      timingAudit,
    };
  }, [timedLyrics]);

  useEffect(() => {
    let cancelled = false;
    apiJson('/auth/me')
      .then((data) => {
        if (!cancelled) setAuthUser(data.user || null);
      })
      .catch(() => {
        if (!cancelled) setAuthUser(null);
      })
      .finally(() => {
        if (!cancelled) setAuthChecked(true);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!authUser?.emailVerified) {
      setLibrary([]);
      return;
    }
    let cancelled = false;
    async function loadUserLibrary() {
      try {
        const migrated = await migrateLocalLibraryToServer();
        const songs = migrated || await loadLibraryRemote();
        if (!cancelled) setLibrary(songs);
      } catch (err) {
        if (!cancelled) setError(`Could not load song library: ${err.message}`);
      }
    }
    loadUserLibrary();
    return () => { cancelled = true; };
  }, [authUser?.emailVerified]);

  useEffect(() => {
    if (!authUser?.emailVerified) return;
    const params = new URLSearchParams(window.location.search);
    if (!params.get('share')) return;
    let cancelled = false;
    readSharedSongFromCurrentUrl()
      .then((song) => {
        if (cancelled || !song) return;
        setSongTitle(song.title || '');
        setLyrics(song.lyrics || '');
        setTimedLyrics(song.timedLyrics || []);
        const { normalized } = normalizeAlignmentWithContext(song.alignment || [], song.lyrics || '', song.timedLyrics || []);
        setAlignment(normalized);
        setSongKey(song.songKey || '');
        setBpm(song.bpm || null);
        setBeatsPerMeasure(song.beatsPerMeasure || null);
        setTransposition(song.transposition || 0);
        setCapo(song.capo || 0);
        setRationalizedAlignment(null);
        setRationalizationCount(0);
        setEditedAlignment(null);
        setRawAnalysis(null);
        if (window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.delete('share');
          window.history.replaceState({}, '', url.toString());
        }
      })
      .catch((err) => {
        if (!cancelled) setError(`Could not load shared song: ${err.message}`);
      });
    return () => { cancelled = true; };
  }, [authUser?.emailVerified, normalizeAlignmentWithContext]);

  // Persist an auto-recovery draft so Safari memory reloads don't lose work.
  const writeRecoveryDraft = useCallback((forcePayload = null) => {
    const activeAlignment = rationalizedAlignment ?? editedAlignment ?? alignment;
    const payload = forcePayload || {
      title: songTitle || '',
      lyrics: lyrics || '',
      alignment: activeAlignment || [],
      timedLyrics: timedLyrics || [],
      songKey: songKey || '',
      bpm: bpm ?? null,
      beatsPerMeasure: beatsPerMeasure ?? null,
      transposition: transposition || 0,
      capo: capo || 0,
      savedAt: Date.now(),
    };
    const hasWork = Boolean(
      (payload.title && String(payload.title).trim())
      || (payload.lyrics && String(payload.lyrics).trim())
      || (Array.isArray(payload.alignment) && payload.alignment.length)
    );
    try {
      if (!hasWork) localStorage.removeItem(DRAFT_STORAGE_KEY);
      else localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      if (e instanceof DOMException && e.name === 'QuotaExceededError') {
        // Storage full — clear the draft to free space rather than silently losing it next time.
        try { localStorage.removeItem(DRAFT_STORAGE_KEY); } catch { /* ignore */ }
      }
    }
  }, [songTitle, lyrics, rationalizedAlignment, editedAlignment, alignment, timedLyrics, songKey, bpm, beatsPerMeasure, transposition, capo]);

  useEffect(() => {
    const t = setTimeout(() => writeRecoveryDraft(), 500);
    return () => clearTimeout(t);
  }, [writeRecoveryDraft]);

  // Use a stable ref so the pagehide/beforeunload handlers are registered only once,
  // while still always calling the latest version of writeRecoveryDraft.
  const writeRecoveryDraftRef = useRef(writeRecoveryDraft);
  useEffect(() => { writeRecoveryDraftRef.current = writeRecoveryDraft; }, [writeRecoveryDraft]);

  useEffect(() => {
    const flush = () => writeRecoveryDraftRef.current();
    window.addEventListener('pagehide', flush);
    window.addEventListener('beforeunload', flush);
    return () => {
      window.removeEventListener('pagehide', flush);
      window.removeEventListener('beforeunload', flush);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Separate cleanup effects — each URL is only revoked when *that* URL changes.
  // A combined effect with all three deps would incorrectly revoke audioObjectUrl
  // when instrumentalAudioObjectUrl or vocalsAudioObjectUrl is set (e.g., after
  // stem separation), making the original file unplayable in Song mode.
  useEffect(() => () => {
    if (audioObjectUrl && String(audioObjectUrl).startsWith('blob:')) {
      URL.revokeObjectURL(audioObjectUrl);
    }
  }, [audioObjectUrl]);

  useEffect(() => () => {
    if (instrumentalAudioObjectUrl && String(instrumentalAudioObjectUrl).startsWith('blob:')) {
      URL.revokeObjectURL(instrumentalAudioObjectUrl);
    }
  }, [instrumentalAudioObjectUrl]);

  useEffect(() => () => {
    if (vocalsAudioObjectUrl && String(vocalsAudioObjectUrl).startsWith('blob:')) {
      URL.revokeObjectURL(vocalsAudioObjectUrl);
    }
  }, [vocalsAudioObjectUrl]);

  // Re-build beat annotations when the user manually changes the time signature.
  // We re-number measure/beat from the stored beat timeline and re-inject carry-forward chords.
  useEffect(() => {
    if (!rawAnalysis?.beats?.length || !beatsPerMeasure) return;
    const rebuildSignature = [
      Number(beatsPerMeasure || 0),
      Number(rawAnalysis?.beats?.length || 0),
      Number(rawAnalysis?.bpm || 0),
      Number(rawAnalysis?.timedLyrics?.length || 0),
      String(rawAnalysis?.file || ''),
    ].join('|');
    if (lastBeatRebuildSignatureRef.current === rebuildSignature) return;
    lastBeatRebuildSignatureRef.current = rebuildSignature;
    const newBeats = rawAnalysis.beats.map((b, idx) => ({
      time: Number(b.time),
      measure: Math.floor(idx / beatsPerMeasure) + 1,
      beat: (idx % beatsPerMeasure) + 1,
    }));
    const baseAl = (rationalizedAlignment ?? editedAlignment ?? alignment ?? rawAnalysis.baseAlignment) || [];
    const enriched = addSectionStartChords(
      baseAl,
      lyrics,
      rawAnalysis.timedLyrics || [],
      rawAnalysis.bpm,
      beatsPerMeasure,
      newBeats,
    );
    const { normalized, diagnostics } = normalizeAlignmentWithContext(
      enriched,
      lyrics,
      rawAnalysis.timedLyrics || timedLyrics,
      { bpm: rawAnalysis?.bpm ?? bpm ?? null },
    );
    if (rationalizedAlignment) setRationalizedAlignment(normalized);
    else if (editedAlignment) setEditedAlignment(normalized);
    else setAlignment(normalized);
    setRawAnalysis(prev => {
      if (!prev) return prev;
      const parity = buildParityDiagnostics(lyrics, normalized, rawAnalysis.timedLyrics || timedLyrics);
      return compactRawAnalysisPayload({
        ...prev,
        beats: newBeats,
        alignment: normalized,
        alignmentDiagnostics: diagnostics,
        anchorDecisionDiagnostics: diagnostics?.perLineAnchorPlacements || {},
        splitDecisionDiagnostics: parity.lineLayouts.map((layout) => ({
          lineIndex: layout.lineIndex,
          splitDecisions: layout.splitDecisions || [],
        })),
        renderParityHash: parity.hash,
        renderParity: {
          web: parity.webHash,
          text: parity.textHash,
          pdf: parity.pdfHash,
          lineCount: parity.lineCount,
        },
        renderParityDiff: parity.diff,
        timingAudit: parity.timingAudit,
      });
    });
  }, [beatsPerMeasure, rawAnalysis, lyrics, timedLyrics, normalizeAlignmentWithContext, buildParityDiagnostics, bpm, rationalizedAlignment, editedAlignment, alignment]);

  // Clear URL param after loading shared song so refresh doesn't re-apply it
  useEffect(() => {
    if (sharedSong && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.delete('song');
      window.history.replaceState({}, '', url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (restoredDraftRef.current) return;
    restoredDraftRef.current = true;
    if (sharedSong) return; // explicit shared link should win
    if (lyrics?.trim() || alignment?.length) return; // already initialized with content
    try {
      const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (!draft || (!draft.lyrics && !(draft.alignment || []).length)) return;
      const { normalized } = normalizeAlignmentWithContext(
        draft.alignment || [],
        draft.lyrics || '',
        draft.timedLyrics || [],
        { bpm: draft.bpm ?? null },
      );
      setSongTitle(draft.title || '');
      setLyrics(draft.lyrics || '');
      setTimedLyrics(draft.timedLyrics || []);
      setAlignment(normalized);
      setSongKey(draft.songKey || '');
      setBpm(draft.bpm ?? null);
      setBeatsPerMeasure(draft.beatsPerMeasure ?? null);
      setTransposition(draft.transposition || 0);
      setCapo(draft.capo || 0);
      setEditedAlignment(null);
      setRationalizedAlignment(null);
      setRationalizationCount(0);
      setError('Recovered unsaved draft after browser reload.');
    } catch {
      // ignore invalid draft payloads
    }
  }, [sharedSong, lyrics, alignment, normalizeAlignmentWithContext]);

  // Fetch musical song segments for all words in current lyrics so that
  // chord drop zones use server-grouped segments (e.g. a-maz-ing) instead
  // of raw per-phoneme boundaries or client-side syllabification.
  useEffect(() => {
    if (!lyrics) return;
    const words = lyrics.match(/[a-zA-Z']+/g);
    if (!words) return;
    fetchPhonemeGraphemes(words);
  }, [lyrics]);

  const handleFileSelect = useCallback((file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['wav', 'mp3', 'mp4', 'm4a'].includes(ext)) {
      setError(`Unsupported file type ".${ext}". Please upload a .wav, .mp3, .mp4, or .m4a file.`);
      return;
    }
    setError('');
    setAudioFileName(file.name);
    // Auto-populate title from filename (strip extension) if no title set yet
    const baseName = file.name.replace(/\.[^.]+$/, '');
    setSongTitle(prev => prev || baseName);
    setPendingFile(file);
    setAudioId(null); // clear any URL-imported audio
    // Create object URL for in-browser playback.
    // MP4 video files (video/mp4) won't play in an <audio> element in some browsers.
    // Re-wrap as audio/mp4 so the browser treats it as audio-only.
    let playbackBlob = file;
    if (file.type === 'video/mp4' || (file.type === '' && /\.mp4$/i.test(file.name))) {
      playbackBlob = new Blob([file], { type: 'audio/mp4' });
    }
    // The useEffect cleanup at the top of App revokes the old blob URL when this changes.
    setAudioObjectUrl(URL.createObjectURL(playbackBlob));
    setInstrumentalAudioObjectUrl(null);
    setVocalsAudioObjectUrl(null);
  }, []);

  const isAudioFile = audioId != null || (pendingFile && !['mid', 'midi'].includes(
    (pendingFile.name.split('.').pop() || '').toLowerCase()
  ));

  const handleMidiChordImport = useCallback(async (file) => {
    const ext = (file?.name || '').split('.').pop().toLowerCase();
    if (!['mid', 'midi'].includes(ext)) {
      setError(`Unsupported MIDI file type ".${ext}". Please upload a .mid or .midi file.`);
      return;
    }
    setError('');
    try {
      const buffer = await file.arrayBuffer();
      const midi = parseMidiChords(buffer, MidiParser);
      const midiEvents = midi.chordEvents || [];
      if (!midiEvents.length) {
        throw new Error('No chords detected in this MIDI file.');
      }
      setMidiChordSource({
        name: file.name,
        events: midiEvents,
        key: midi.key || '',
        bpm: midi.bpm || null,
      });
      if (midi.key) setSongKey(midi.key);
      if (midi.bpm && !bpm) setBpm(midi.bpm);
    } catch (err) {
      setError(`MIDI import failed: ${err.message}`);
    }
  }, [bpm]);

  const handleAiTranscribe = useCallback(async () => {
    if (!pendingFile && !audioId) return;
    setTranscribing(true);
    setError('');
    try {
      let data;
      if (audioId) {
        // URL-imported audio: use cached file on server
        const resp = await apiFetch('/transcribe-cached', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ audioId }),
        });
        if (!resp.ok) throw new Error(`Backend returned ${resp.status}`);
        data = await resp.json();
      } else {
        const formData = new FormData();
        formData.append('audio', pendingFile);
        const resp = await apiFetch('/transcribe', {
          method: 'POST',
          body: formData,
        });
        if (!resp.ok) throw new Error(`Backend returned ${resp.status}`);
        data = await resp.json();
      }
      if (data.error) throw new Error(data.error);
      // Use per-segment lines for natural line breaks; fall back to flat transcript
      const rawLines = data.lines && data.lines.length > 0 ? data.lines : [];
      const rawText = rawLines.length > 0
        ? rawLines.map(l => l.text).join('\n')
        : (data.transcript || '');
      const lyricLines = rawText.split('\n').filter(l => l.trim());

      let taggedLyrics;
      const ss = data.sectionStructure;
      if (ss && ss.boundaries && ss.clusterIds && ss.clusterIds.length >= 2) {
        // Audio structural analysis available — use it for section labeling
        taggedLyrics = addSectionTagsFromBoundaries(
          lyricLines, ss.boundaries, ss.clusterIds,
          rawLines, data.instrumentalRanges || []
        );
      } else {
        // Fall back to text-based algorithm
        taggedLyrics = addSectionTags(rawText);
        // Inject [Intro]/[Instrumental] tags for silent sections (no singing)
        taggedLyrics = injectInstrumentalTags(taggedLyrics, data.instrumentalRanges || [], rawLines);
      }
      setLyrics(taggedLyrics);
    } catch (err) {
      if (err.message.includes('Failed to fetch')) {
        setError('Could not reach the analysis backend. Is the server running? Run: npm run dev');
      } else {
        setError(`Transcription failed: ${err.message}`);
      }
    } finally {
      setTranscribing(false);
    }
  }, [pendingFile, audioId]);

  /** Read an SSE stream from a fetch Response, calling onProgress and onResult callbacks. */
  const readSseStream = useCallback(async (resp, onProgress, onResult, signal) => {
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let eventType = '';

    try {
      while (true) {
        if (signal?.aborted) { reader.cancel('cancelled'); break; }
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete trailing line

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim();
          } else if (line.startsWith('data:')) {
            const data = JSON.parse(line.slice(5).trim());
            if (eventType === 'progress') {
              onProgress(data);
            } else if (eventType === 'result') {
              onResult(data);
              return;
            } else if (eventType === 'error') {
              throw new Error(data.error || 'Analysis failed');
            }
          } else if (line === '') {
            eventType = '';
          }
        }
      }
    } finally {
      // no-op; analysis streams are allowed to run until result, error, or user cancel
    }
  }, []);

  const handleDetect = useCallback(async () => {
    if (!pendingFile && !audioId && !lyrics) return;

    // Cancel any in-flight detection
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const signal = controller.signal;

    setDetecting(true);
    setAnalysisProgress({ step: 'Starting...', pct: 1 });
    setError('');
    setRationalizedAlignment(null); // reset on new detection
    setRationalizationCount(0);

    // Helper to process the raw analysis result (shared by file upload + cached audio paths)
    const processAnalysisResult = (data, sourceName) => {
      setAnalysisProgress({ step: 'Aligning lyrics to chords...', pct: 97 });
      const useMidiChords = !!(midiChordSource?.events?.length);
      const chords = useMidiChords ? midiChordSource.events : (data.chords || []);
      const effectiveKey = useMidiChords ? (midiChordSource?.key || data.key || '') : (data.key || '');
      const effectiveBpm = useMidiChords ? (midiChordSource?.bpm || data.bpm || null) : (data.bpm || null);
      setSongKey(effectiveKey);
      setBpm(effectiveBpm);
      setBeatsPerMeasure(data.beatsPerMeasure || null);

      const whisperWords = data.whisper?.words || null;
      const whisperLines = data.whisper?.lines || [];
      let activeLyrics = lyrics;
      if (!activeLyrics?.trim() && data.whisper?.transcript) {
        // No user-provided lyrics — use Whisper transcription with section tags
        const rawText = whisperLines.length > 0
          ? whisperLines.map(l => l.text).join('\n')
          : data.whisper.transcript;
        const lyricLines = rawText.split('\n').filter(l => l.trim());
        const ss = data.sectionStructure;
        if (ss && ss.boundaries && ss.clusterIds && ss.clusterIds.length >= 2) {
          activeLyrics = addSectionTagsFromBoundaries(
            lyricLines, ss.boundaries, ss.clusterIds,
            whisperLines, data.instrumentalRanges || []
          );
        } else {
          let tagged = addSectionTags(rawText);
          tagged = injectInstrumentalTags(tagged, data.instrumentalRanges || [], whisperLines);
          activeLyrics = tagged;
        }
        setLyrics(activeLyrics);
      }

      const sixteenth = effectiveBpm ? (60 / effectiveBpm / 4) : 0.125;
      const filteredChords = chords.filter(c => !c.end || (c.end - c.time) >= sixteenth);

      const { alignment: rawAlign, orphans, introAssignmentDiagnostics, wordTimingRepairs } = alignChordsToLyrics(filteredChords, activeLyrics, whisperWords, { sixteenthSec: sixteenth });
      const nextTimedLyrics = whisperWords ? computeTimedLyrics(activeLyrics, whisperWords, { chords: filteredChords, sixteenthSec: sixteenth }) : [];
      const withSpacers = injectSpacers(orphans, rawAlign, nextTimedLyrics, activeLyrics);
      const beats = useMidiChords ? [] : (data.beats || []);
      const enrichedAlignment = addSectionStartChords(withSpacers, activeLyrics, nextTimedLyrics, effectiveBpm || null, data.beatsPerMeasure || null, beats);
      const { normalized, diagnostics } = normalizeAlignmentWithContext(
        enrichedAlignment,
        activeLyrics,
        nextTimedLyrics,
        { bpm: effectiveBpm || null },
      );
      const parity = buildParityDiagnostics(activeLyrics, normalized, nextTimedLyrics);
      setTimedLyrics(nextTimedLyrics);
      setAlignment(normalized);
      setRationalizedAlignment(null);
      setRationalizationCount(0);
      setEditedAlignment(null);

      setRawAnalysis(compactRawAnalysisPayload({
        file: sourceName,
        key: effectiveKey,
        bpm: effectiveBpm,
        beatsPerMeasure: data.beatsPerMeasure || null,
        beats,
        rawBeatTimes: beats.map(b => b.time),
        baseAlignment: normalized,
        chords: filteredChords,
        allChords: chords,
        sourceDetectedChords: data.chords || [],
        chordSource: useMidiChords ? 'external_midi' : 'audio_detection',
        midiChordFile: useMidiChords ? midiChordSource.name : null,
        whisperTranscript: data.whisper?.transcript || '',
        whisperWords: whisperWords || [],
        timedLyrics: nextTimedLyrics,
        alignment: normalized,
        alignmentDiagnostics: diagnostics,
        anchorDecisionDiagnostics: diagnostics?.perLineAnchorPlacements || {},
        splitDecisionDiagnostics: parity.lineLayouts.map((layout) => ({
          lineIndex: layout.lineIndex,
          splitDecisions: layout.splitDecisions || [],
        })),
        introAssignmentDiagnostics: introAssignmentDiagnostics || null,
        wordTimingRepairs: wordTimingRepairs || nextTimedLyrics.wordTimingRepairs || [],
        beatSource: data.beatSource || null,
        beatSourceScores: data.beatSourceScores || null,
        bpmCandidates: data.bpmCandidates || null,
        anchorPlacements: parity.lineLayouts.map((layout) => ({
          lineIndex: layout.lineIndex,
          anchors: layout.alignedEvents || [],
        })),
        renderParityHash: parity.hash,
        renderParity: {
          web: parity.webHash,
          text: parity.textHash,
          pdf: parity.pdfHash,
          lineCount: parity.lineCount,
        },
        renderParityDiff: parity.diff,
        timingAudit: parity.timingAudit,
        strumPattern: data.strumPattern || null,
      }));
      setInstrumentalAudioObjectUrl(
        data.instrumentalAudioId ? apiUrl(`/audio/${data.instrumentalAudioId}`) : null
      );
      setVocalsAudioObjectUrl(
        data.vocalsAudioId ? apiUrl(`/audio/${data.vocalsAudioId}`) : null
      );
      if (useMidiChords && midiChordSource.key) setSongKey(midiChordSource.key);
    };

    try {
      if (audioId && !pendingFile) {
        // Analyze server-cached audio from URL import
        const resp = await apiFetch('/analyze-audio-cached', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ audioId, lyrics: lyrics?.trim() || '', stemSeparate }),
          signal,
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.error || `Backend returned ${resp.status}`);
        }

        let data;
        await readSseStream(
          resp,
          (progress) => setAnalysisProgress(progress),
          (result) => { data = result; },
          signal,
        );
        if (!data) throw new Error('No result received from analysis');
        if (data.error) throw new Error(data.error);
        processAnalysisResult(data, audioFileName);

      } else if (pendingFile) {
        const ext = pendingFile.name.split('.').pop().toLowerCase();

        if (['wav', 'mp3', 'mp4', 'm4a'].includes(ext)) {
          // POST to backend — response is an SSE stream
          const formData = new FormData();
          formData.append('audio', pendingFile);
          if (lyrics && lyrics.trim()) formData.append('lyrics', lyrics.trim());
          if (stemSeparate) formData.append('stemSeparate', 'true');

          const resp = await apiFetch('/analyze-audio', {
            method: 'POST',
            body: formData,
            signal,
          });
          if (!resp.ok) throw new Error(`Backend returned ${resp.status}`);

          let data;
          await readSseStream(
            resp,
            (progress) => setAnalysisProgress(progress),
            (result) => { data = result; },
            signal,
          );
          if (!data) throw new Error('No result received from analysis');
          if (data.error) throw new Error(data.error);
          if (data.warning) console.warn(data.warning);
          if (data.chordsError) console.warn('Chord detection error:', data.chordsError);

          processAnalysisResult(data, audioFileName);
        } else {
          // MIDI: in-browser
          setAnalysisProgress({ step: 'Parsing MIDI...', pct: 50 });
          const buffer = await pendingFile.arrayBuffer();
          const midi = parseMidiChords(buffer, MidiParser);
          const midiEvents = midi.chordEvents || [];
          if (midiEvents.length === 0) {
            setError('No chords detected in this MIDI file. Try a file with chordal content.');
          } else {
            let activeLyrics = lyrics || '';
            const lyricLines = activeLyrics.split('\n').map((line) => line.trim());
            const hasLyricWords = lyricLines.some((line) => line && !/^\[.+\]$/.test(line));
            if (!hasLyricWords) {
              activeLyrics = '[Instrumental]';
              setLyrics(activeLyrics);
            }

            let baseAlignment = [];
            let orphanChords = [];
            if (hasLyricWords) {
              const timedWordRefs = (timedLyrics || [])
                .filter((w) => w && w.start != null && w.end != null && w.word)
                .sort((a, b) => {
                  if (a.lineIndex !== b.lineIndex) return a.lineIndex - b.lineIndex;
                  return a.wordIndex - b.wordIndex;
                })
                .map((w) => ({ word: w.word, start: Number(w.start), end: Number(w.end) }));
              const aligned = alignChordsToLyrics(
                midiEvents,
                activeLyrics,
                timedWordRefs.length ? timedWordRefs : null,
              );
              baseAlignment = aligned.alignment || [];
              orphanChords = aligned.orphans || [];
            } else {
              baseAlignment = midiEvents.map((evt, idx) => ({
                chord: evt.chord,
                lineIndex: 0,
                wordIndex: idx,
                time: evt.time,
              }));
            }

            const withSpacers = hasLyricWords
              ? injectSpacers(orphanChords, baseAlignment, timedLyrics || [], activeLyrics)
              : baseAlignment;
            const enriched = addSectionStartChords(
              withSpacers,
              activeLyrics,
              timedLyrics || [],
              midi.bpm || null,
              beatsPerMeasure || null,
              [],
            );
            const { normalized, diagnostics } = normalizeAlignmentWithContext(
              enriched,
              activeLyrics,
              timedLyrics,
              { bpm: midi.bpm || null },
            );
            const parity = buildParityDiagnostics(activeLyrics, normalized);

            if (midi.key) setSongKey(midi.key);
            if (midi.bpm) setBpm(midi.bpm);
            setAlignment(normalized);
            setRationalizedAlignment(null);
            setRationalizationCount(0);
            setEditedAlignment(null);
            setRawAnalysis(compactRawAnalysisPayload({
              file: pendingFile.name,
              key: midi.key || '',
              bpm: midi.bpm || null,
              beatsPerMeasure: beatsPerMeasure || null,
              beats: [],
              rawBeatTimes: [],
              baseAlignment: normalized,
              chords: midiEvents,
              allChords: midiEvents,
              whisperTranscript: '',
              whisperWords: [],
              timedLyrics: timedLyrics || [],
              alignment: normalized,
              alignmentDiagnostics: diagnostics,
              anchorDecisionDiagnostics: diagnostics?.perLineAnchorPlacements || {},
              splitDecisionDiagnostics: parity.lineLayouts.map((layout) => ({
                lineIndex: layout.lineIndex,
                splitDecisions: layout.splitDecisions || [],
              })),
              beatSource: 'midi',
              beatSourceScores: null,
              bpmCandidates: null,
              anchorPlacements: parity.lineLayouts.map((layout) => ({
                lineIndex: layout.lineIndex,
                anchors: layout.alignedEvents || [],
              })),
              renderParityHash: parity.hash,
              renderParity: {
                web: parity.webHash,
                text: parity.textHash,
                pdf: parity.pdfHash,
                lineCount: parity.lineCount,
              },
              renderParityDiff: parity.diff,
              timingAudit: parity.timingAudit,
            }));
            setInstrumentalAudioObjectUrl(null);
        setVocalsAudioObjectUrl(null);
          }
        }
      } else if (midiChordSource?.events?.length) {
        // External MIDI chords without an audio source
        const activeLyrics = (lyrics && lyrics.trim()) ? lyrics : '[Instrumental]';
        if (!lyrics?.trim()) setLyrics(activeLyrics);
        const midiEvents = midiChordSource.events || [];
        let baseAlignment = [];
        let orphanChords = [];
        if (activeLyrics !== '[Instrumental]') {
          const timedWordRefs = (timedLyrics || [])
            .filter((w) => w && w.start != null && w.end != null && w.word)
            .sort((a, b) => (a.lineIndex - b.lineIndex) || (a.wordIndex - b.wordIndex))
            .map((w) => ({ word: w.word, start: Number(w.start), end: Number(w.end) }));
          const aligned = alignChordsToLyrics(
            midiEvents,
            activeLyrics,
            timedWordRefs.length ? timedWordRefs : null,
          );
          baseAlignment = aligned.alignment || [];
          orphanChords = aligned.orphans || [];
        } else {
          baseAlignment = midiEvents.map((evt, idx) => ({
            chord: evt.chord,
            lineIndex: 0,
            wordIndex: idx,
            time: evt.time,
          }));
        }
        const withSpacers = activeLyrics !== '[Instrumental]'
          ? injectSpacers(orphanChords, baseAlignment, timedLyrics || [], activeLyrics)
          : baseAlignment;
        const enriched = addSectionStartChords(
          withSpacers,
          activeLyrics,
          timedLyrics || [],
          midiChordSource.bpm || null,
          beatsPerMeasure || null,
          [],
        );
        const { normalized, diagnostics } = normalizeAlignmentWithContext(
          enriched,
          activeLyrics,
          timedLyrics,
          { bpm: midiChordSource.bpm || null },
        );
        const parity = buildParityDiagnostics(activeLyrics, normalized);
        if (midiChordSource.key) setSongKey(midiChordSource.key);
        if (midiChordSource.bpm) setBpm(midiChordSource.bpm);
        setAlignment(normalized);
        setRationalizedAlignment(null);
        setRationalizationCount(0);
        setEditedAlignment(null);
        setRawAnalysis(compactRawAnalysisPayload({
          file: midiChordSource.name,
          key: midiChordSource.key || '',
          bpm: midiChordSource.bpm || null,
          beatsPerMeasure: beatsPerMeasure || null,
          beats: [],
          rawBeatTimes: [],
          baseAlignment: normalized,
          chords: midiEvents,
          allChords: midiEvents,
          sourceDetectedChords: [],
          chordSource: 'external_midi',
          midiChordFile: midiChordSource.name,
          whisperTranscript: '',
          whisperWords: [],
          timedLyrics: timedLyrics || [],
          alignment: normalized,
          alignmentDiagnostics: diagnostics,
          anchorDecisionDiagnostics: diagnostics?.perLineAnchorPlacements || {},
          splitDecisionDiagnostics: parity.lineLayouts.map((layout) => ({
            lineIndex: layout.lineIndex,
            splitDecisions: layout.splitDecisions || [],
          })),
          beatSource: 'midi',
          beatSourceScores: null,
          bpmCandidates: null,
          anchorPlacements: parity.lineLayouts.map((layout) => ({
            lineIndex: layout.lineIndex,
            anchors: layout.alignedEvents || [],
          })),
          renderParityHash: parity.hash,
          renderParity: {
            web: parity.webHash,
            text: parity.textHash,
            pdf: parity.pdfHash,
            lineCount: parity.lineCount,
          },
          renderParityDiff: parity.diff,
          timingAudit: parity.timingAudit,
        }));
        setInstrumentalAudioObjectUrl(null);
        setVocalsAudioObjectUrl(null);
      } else if (lyrics) {
        // Lyrics only — nothing to detect yet; clear alignment
        setAlignment([]);
        setTimedLyrics([]);
        setInstrumentalAudioObjectUrl(null);
        setVocalsAudioObjectUrl(null);
      }
    } catch (err) {
      if (err.name === 'AbortError' || signal.aborted) {
        // User cancelled — no error message
      } else if (err.message.includes('Failed to fetch') || err.message.includes('Backend')) {
        setError('Could not reach the analysis backend. Is the server running?');
      } else {
        setError(`Detection failed: ${err.message}`);
      }
    } finally {
      setDetecting(false);
      setAnalysisProgress(null);
      if (abortControllerRef.current?.signal === signal) abortControllerRef.current = null;
    }
  }, [pendingFile, audioId, lyrics, audioFileName, stemSeparate, readSseStream, normalizeAlignmentWithContext, timedLyrics, buildParityDiagnostics, beatsPerMeasure, midiChordSource]);

  const handleUrlImport = useCallback(async (url, onSuccess) => {
    if (!url) return;
    setImportingUrl(true);
    setAnalysisProgress({ step: 'Downloading audio…', pct: 5 });
    setError('');
    try {
      const resp = await apiFetch('/download-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });
      if (!resp.ok) throw new Error(`Backend returned ${resp.status}`);

      let data;
      await readSseStream(
        resp,
        (progress) => setAnalysisProgress(progress),
        (result) => { data = result; },
      );
      if (!data) throw new Error('No result received');
      if (data.error) throw new Error(data.error);

      const urlName = (() => { try { return new URL(url).hostname; } catch { return 'url-import'; } })();
      setAudioFileName(urlName);
      setPendingFile(null);
      setAudioId(data.audioId);
      // The useEffect cleanup at the top of App revokes any previous blob URL when audioObjectUrl changes.
      setAudioObjectUrl(apiUrl(`/audio/${data.audioId}`));
      setInstrumentalAudioObjectUrl(null);
      setVocalsAudioObjectUrl(null);
      // Clear any prior analysis so the user can review lyrics before detecting
      setAlignment([]);
      setEditedAlignment(null);
      setRationalizedAlignment(null);
      setRationalizationCount(0);
      setRawAnalysis(null);
      setTimedLyrics([]);
      setMidiChordSource(null);

      if (onSuccess) onSuccess();
    } catch (err) {
      setError(`URL import failed: ${err.message}`);
    } finally {
      setImportingUrl(false);
      setAnalysisProgress(null);
    }
  }, [readSseStream]);

  const handleRationalize = useCallback(() => {
    if (rationalizedAlignment) {
      // Toggle off — revert to pre-rationalize state
      setRationalizedAlignment(null);
      setRationalizationCount(0);
    } else {
      // Rationalize on top of any manual edits
      const base = editedAlignment ?? alignment;
      const { alignment: rationalized, corrections, diagnostics } = rationalizeAlignment(
        base, lyrics, songKey, timedLyrics, rawAnalysis?.beats || []
      );
      if (typeof window !== 'undefined' && window.console) {
        Object.entries(diagnostics || {}).forEach(([type, info]) => {
          const refSeq = (info?.referenceSequence || []).map((s) => `${s.chord}@L${s.relLine}:W${s.wordIndex}`);
          const consensusSeq = (info?.consensusTemplate || []).map((s) =>
            `${s.chord}@L${s.relLine}:W${s.wordIndex} [${s.winCount}/${s.instanceCount}]`
          );
          // eslint-disable-next-line no-console
          console.groupCollapsed(`[Rationalize] ${type} reference (${refSeq.length})`);
          // eslint-disable-next-line no-console
          console.log('referenceSequence', refSeq);
          // eslint-disable-next-line no-console
          console.groupEnd();
          // eslint-disable-next-line no-console
          console.groupCollapsed(`[Rationalize] ${type} consensus (${consensusSeq.length})`);
          // eslint-disable-next-line no-console
          console.log('consensusTemplate', consensusSeq);
          // eslint-disable-next-line no-console
          console.log('referenceSection', info?.referenceSection || null);
          // eslint-disable-next-line no-console
          console.log('summary', info?.summary || null);
          // eslint-disable-next-line no-console
          console.log('consensusColumns', info?.consensusColumns || []);
          // eslint-disable-next-line no-console
          console.log('instanceSequences', info?.instanceSequences || []);
          // eslint-disable-next-line no-console
          console.groupEnd();
        });
      }
      const { normalized } = normalizeAlignmentWithContext(rationalized, lyrics, timedLyrics);
      setRationalizedAlignment(normalized);
      setRationalizationCount(corrections);
    }
  }, [rationalizedAlignment, editedAlignment, alignment, lyrics, songKey, rawAnalysis, timedLyrics, normalizeAlignmentWithContext]);

  const handleAlignmentEdit = useCallback((newAlignment) => {
    const { normalized, diagnostics } = normalizeAlignmentWithContext(newAlignment, lyrics, timedLyrics);
    if (process.env.NODE_ENV !== 'production') {
      const latestManual = [...(newAlignment || [])]
        .reverse()
        .find((e) => String(e?.anchorRule || '').startsWith('manual-slot-drop'));
      if (latestManual) {
        const match = (normalized || []).find((n) => (
          String(n?.chord || '') === String(latestManual?.chord || '')
          && Number(n?.lineIndex) === Number(latestManual?.lineIndex)
          && Math.abs(Number(n?.time ?? -9999) - Number(latestManual?.time ?? -9999)) < 0.02
        ));
        // eslint-disable-next-line no-console
        console.debug('[ChordDrop:normalized]', {
          input: {
            chord: latestManual?.chord,
            lineIndex: latestManual?.lineIndex,
            wordIndex: latestManual?.wordIndex,
            syllableOffset: latestManual?.syllableOffset ?? 0,
            time: latestManual?.time ?? null,
            anchorRule: latestManual?.anchorRule,
            gapProgress: latestManual?.gapProgress ?? null,
          },
          normalized: match ? {
            lineIndex: match.lineIndex,
            wordIndex: match.wordIndex,
            syllableOffset: match.syllableOffset ?? 0,
            time: match.time ?? null,
            anchorRule: match.anchorRule ?? null,
            anchorMethod: match.anchorMethod ?? null,
          } : null,
        });
      }
    }
    setEditedAlignment(normalized);
    setRawAnalysis(prev => {
      if (!prev) return prev;
      const parity = buildParityDiagnostics(lyrics, normalized, timedLyrics);
      return compactRawAnalysisPayload({
        ...prev,
        alignmentDiagnostics: diagnostics,
        anchorDecisionDiagnostics: diagnostics?.perLineAnchorPlacements || {},
        splitDecisionDiagnostics: parity.lineLayouts.map((layout) => ({
          lineIndex: layout.lineIndex,
          splitDecisions: layout.splitDecisions || [],
        })),
        alignment: normalized,
        renderParityHash: parity.hash,
        renderParity: {
          web: parity.webHash,
          text: parity.textHash,
          pdf: parity.pdfHash,
          lineCount: parity.lineCount,
        },
        renderParityDiff: parity.diff,
        timingAudit: parity.timingAudit,
      });
    });
    // Manual edits supersede any active rationalization
    if (rationalizedAlignment) {
      setRationalizedAlignment(null);
      setRationalizationCount(0);
    }
  }, [rationalizedAlignment, normalizeAlignmentWithContext, lyrics, timedLyrics, buildParityDiagnostics]);

  const handleTimedLyricsChange = useCallback((nextTimedLyrics) => {
    const safeTimed = Array.isArray(nextTimedLyrics) ? nextTimedLyrics : [];
    const timingEditDiagnostics = buildTimingEditDiagnostics(timedLyrics || [], safeTimed, null);
    setTimedLyrics(safeTimed);
    const base = rationalizedAlignment ?? editedAlignment ?? alignment;
    const { normalized, diagnostics } = normalizeAlignmentWithContext(base, lyrics, safeTimed);
    if (rationalizedAlignment) setRationalizedAlignment(normalized);
    else if (editedAlignment) setEditedAlignment(normalized);
    else setAlignment(normalized);
    setRawAnalysis(prev => {
      if (!prev) return prev;
      const parity = buildParityDiagnostics(lyrics, normalized, safeTimed);
      return compactRawAnalysisPayload({
        ...prev,
        timedLyrics: safeTimed,
        alignment: normalized,
        alignmentDiagnostics: diagnostics,
        anchorDecisionDiagnostics: diagnostics?.perLineAnchorPlacements || {},
        splitDecisionDiagnostics: parity.lineLayouts.map((layout) => ({
          lineIndex: layout.lineIndex,
          splitDecisions: layout.splitDecisions || [],
        })),
        renderParityHash: parity.hash,
        renderParity: {
          web: parity.webHash,
          text: parity.textHash,
          pdf: parity.pdfHash,
          lineCount: parity.lineCount,
        },
        renderParityDiff: parity.diff,
        timingAudit: parity.timingAudit,
        timingEditDiagnostics,
      });
    });
  }, [rationalizedAlignment, editedAlignment, alignment, normalizeAlignmentWithContext, lyrics, buildParityDiagnostics, timedLyrics]);

  // Active alignment priority: rationalized (when active) > manual edits > raw detection
  const displayAlignment = useMemo(() => {
    const base = rationalizedAlignment ?? editedAlignment ?? alignment;
    const { normalized } = normalizeAlignmentWithContext(base, lyrics, timedLyrics);
    return normalized;
  }, [rationalizedAlignment, editedAlignment, alignment, normalizeAlignmentWithContext, lyrics, timedLyrics]);

  // Derive display title — prefer user-set title, fall back to filename, then 'Untitled Song'
  const title = songTitle || (audioFileName ? audioFileName.replace(/\.[^.]+$/, '') : 'Untitled Song');

  // Handle .txt chord sheet import
  const handleChordSheetImport = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = parseChordSheet(e.target.result);
        if (parsed.title) setSongTitle(parsed.title);
        if (parsed.songKey) setSongKey(parsed.songKey);
        setCapo(parsed.capo || 0);
        setBpm(parsed.bpm || null);
        setTransposition(0);
        setLyrics(parsed.lyrics || '');
        setTimedLyrics(parsed.timedLyrics || []);
        const { normalized } = normalizeAlignmentWithContext(parsed.alignment || [], parsed.lyrics || '', parsed.timedLyrics || []);
        setAlignment(normalized);
        setRationalizedAlignment(null);
        setRationalizationCount(0);
        setEditedAlignment(null);
        setRawAnalysis(null);
        setInstrumentalAudioObjectUrl(null);
        setVocalsAudioObjectUrl(null);
        setAudioFileName('');
        setPendingFile(null);
        setAudioId(null);
        setMidiChordSource(null);
      } catch (err) {
        setError(`Failed to parse chord sheet: ${err.message}`);
      }
    };
    reader.readAsText(file);
  }, [normalizeAlignmentWithContext]);

  const handleSave = useCallback(async () => {
    try {
      const entry = await saveSongRemote({
        title,
        lyrics,
        alignment: displayAlignment,
        timedLyrics,
        songKey,
        bpm,
        beatsPerMeasure,
        transposition,
        capo,
      });
      setLibrary(await loadLibraryRemote());
      console.info(`Saved: ${entry.title}`);
    } catch (err) {
      setError(`Save failed: ${err.message}`);
    }
  }, [title, lyrics, displayAlignment, timedLyrics, songKey, bpm, beatsPerMeasure, transposition, capo]);

  const handleLoadSong = useCallback((song) => {
    setSongTitle(song.title || '');
    setLyrics(song.lyrics || '');
    setTimedLyrics(song.timedLyrics || []);
    const { normalized } = normalizeAlignmentWithContext(song.alignment || [], song.lyrics || '', song.timedLyrics || []);
    setAlignment(normalized);
    setSongKey(song.songKey || '');
    setBpm(song.bpm || null);
    setBeatsPerMeasure(song.beatsPerMeasure || null);
    setTransposition(song.transposition || 0);
    setCapo(song.capo || 0);
    setRationalizedAlignment(null);
    setRationalizationCount(0);
    setEditedAlignment(null);
    setRawAnalysis(null);
    setInstrumentalAudioObjectUrl(null);
    setVocalsAudioObjectUrl(null);
    setAudioFileName('');
    setPendingFile(null);
    setAudioId(null);
    setMidiChordSource(null);
    // The useEffect cleanup at the top of App revokes any previous blob URL when audioObjectUrl changes.
    setAudioObjectUrl(null);
  }, [normalizeAlignmentWithContext]);

  const handleReset = useCallback(() => {
    // Cancel any in-flight detection
    if (abortControllerRef.current) abortControllerRef.current.abort();
    // Clear all song state
    setSongTitle('');
    setLyrics('');
    setAlignment([]);
    setSongKey('');
    setBpm(null);
    setBeatsPerMeasure(null);
    setTimedLyrics([]);
    setTransposition(0);
    setCapo(0);
    setRationalizedAlignment(null);
    setRationalizationCount(0);
    setEditedAlignment(null);
    setRawAnalysis(null);
    setAudioFileName('');
    setPendingFile(null);
    setAudioId(null);
    setMidiChordSource(null);
    setAudioObjectUrl(null);
    setInstrumentalAudioObjectUrl(null);
    setVocalsAudioObjectUrl(null);
    setError('');
    setDetecting(false);
    setAnalysisProgress(null);
    try { localStorage.removeItem(DRAFT_STORAGE_KEY); } catch { /* ignore */ }
  }, []);

  if (!authChecked) {
    return <div className="app-shell"><div className="app-body">Loading...</div></div>;
  }

  if (!authUser?.emailVerified) {
    return (
      <AuthPage
        user={authUser}
        onAuthenticated={setAuthUser}
      />
    );
  }

  return (
    <div className="app-shell">
      <Header
        theme={theme}
        onThemeToggle={toggleTheme}
        user={authUser}
        onLogout={async () => {
          try { await apiJson('/auth/logout', { method: 'POST' }); } catch {}
          setAuthUser(null);
        }}
      />
      <div className="app-body">
        <LeftPanel
          lyrics={lyrics}
          onLyricsChange={setLyrics}
          onFileSelect={handleFileSelect}
          onMidiChordImport={handleMidiChordImport}
          onDetect={handleDetect}
          detecting={detecting}
          audioFileName={audioFileName}
          midiChordFileName={midiChordSource?.name || ''}
          isAudioFile={isAudioFile}
          onAiTranscribe={handleAiTranscribe}
          transcribing={transcribing}
          rawAnalysis={rawAnalysis}
          stemSeparate={stemSeparate}
          onStemSeparateChange={setStemSeparate}
          onUrlImport={handleUrlImport}
          importingUrl={importingUrl}
          onChordSheetImport={handleChordSheetImport}
          onReset={handleReset}
        />
        <RightPanel
          lyrics={lyrics}
          alignment={displayAlignment}
          songKey={songKey}
          bpm={bpm}
          onBpmChange={setBpm}
          beatsPerMeasure={beatsPerMeasure}
          onBeatsPerMeasureChange={setBeatsPerMeasure}
          transposition={transposition}
          capo={capo}
          onTranspose={setTransposition}
          onCapoChange={setCapo}
          simplified={simplified}
          onSimplify={setSimplified}
          highlightWordsDuringPlayback={highlightWordsDuringPlayback}
          onHighlightWordsDuringPlaybackChange={setHighlightWordsDuringPlayback}
          timingEditMode={timingEditMode}
          onTimingEditModeChange={setTimingEditMode}
          autoFollowPlayback={autoFollowPlayback}
          onAutoFollowPlaybackChange={setAutoFollowPlayback}
          detecting={detecting}
          onCancelDetect={() => { if (abortControllerRef.current) abortControllerRef.current.abort(); }}
          analysisProgress={analysisProgress}
          error={error}
          title={title}
          onTitleChange={setSongTitle}
          onRationalize={handleRationalize}
          isRationalized={!!rationalizedAlignment}
          rationalizationCount={rationalizationCount}
          onAlignmentChange={handleAlignmentEdit}
          audioObjectUrl={audioObjectUrl}
          instrumentalAudioObjectUrl={instrumentalAudioObjectUrl}
          vocalsAudioObjectUrl={vocalsAudioObjectUrl}
          rawChords={rawAnalysis?.chords || []}
          rawAnalysis={rawAnalysis}
          timedLyrics={timedLyrics}
          onTimedLyricsChange={handleTimedLyricsChange}
          onSave={handleSave}
          onOpenLibrary={() => setLibraryOpen(true)}
          hasSong={alignment.length > 0 || !!lyrics}
        />
        <SongLibrary
          open={libraryOpen}
          onClose={() => setLibraryOpen(false)}
          onLoad={handleLoadSong}
          library={library}
          onLibraryChange={setLibrary}
        />
      </div>
    </div>
  );
}

export default App;
